<script lang="ts" setup>
import { useBaseView, useNotFound, useTruck } from "@/composables";
import TruckViewSidebar from "@/views/fleets/components/TruckViewSidebar.vue";
import SidebarLayout from "@/components/layout/SidebarLayout.vue";
import SidebarToggle from "@/components/buttons/SidebarToggle.vue";
import NotFound from "@/views/notFound/NotFound.vue";
import TruckAdditionalActions from "@/views/fleets/components/TruckAdditionalActions.vue";
import TruckTrackingStatus from "@/components/statuses/TruckTrackingStatus.vue";
import { computed, onMounted, ref, watch } from "vue";
import type { TruckDto, TruckWithCoordinates } from "@/api/trucks/dto/truck.dto";
import { useRoute, useRouter } from "vue-router";
import { API } from "@/api/api";
import OmniTabs from "@/components/omni/OmniTabs.vue";
import { TruckViewTab } from "@/constants/truck";
import TruckActiveRoutes from "@/views/fleets/components/TruckActiveRoutes.vue";
import OmniTooltip from "@/components/omni/OmniTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { useI18n } from "vue-i18n";

const api = new API();
const router = useRouter();

const route = useRoute();
const { entityNotFound, validateEntityResponse } = useNotFound();
const { hasAccess } = useBaseView();
const { t } = useI18n();
const { doorTypeToString, equipmentToString, additionalEquipmentToString } = useTruck();

const truck = ref<TruckWithCoordinates | null>(null);
const loading = ref<boolean>(true);
const tab = ref<TruckViewTab>(TruckViewTab.general);

const editLink = computed(() => {
  const name = route.name;

  if (typeof name === "string" && truck.value) {
    return {
      name: name.replace("view", "edit"),
      params: { id: truck.value.id },
    };
  }

  return null;
});

const hasAccessToEdit = computed(() => {
  return hasAccess(DispatcherPermissionValue.EditOrCreateTruck);
});

function isSidebarOpenDefault() {
  const windowWidth = window.innerWidth;
  return windowWidth > 1250;
}

async function loadTruck() {
  loading.value = true;
  const resp = await api.trucks.findTruckById(String(route.params.id));
  if (!validateEntityResponse<TruckDto>(resp) || !resp.data) {
    return;
  }

  const truckWithCoordinators: TruckWithCoordinates = { coordinators: [], ...resp.data };

  if (!truckWithCoordinators.ownerId) {
    return;
  }

  // load truck owner coordinators
  const coordinatorsResp = await api.users.findOwnerCoordinators(truckWithCoordinators.ownerId);
  if (coordinatorsResp.success && coordinatorsResp.data) {
    truckWithCoordinators.coordinators = coordinatorsResp.data.filter((c) => c.fullAccess);
  }

  truck.value = truckWithCoordinators;
  loading.value = false;
}

function setTabByDefault() {
  const hash = route.hash.replace("#", "");

  switch (hash) {
    case "active-routes":
      tab.value = TruckViewTab.activeRoutes;
      return;
    case "completed-routes":
      tab.value = TruckViewTab.completedRoutes;
      return;
    default:
      tab.value = TruckViewTab.general;
  }
}

watch(tab, (value) => {
  if (value === TruckViewTab.activeRoutes) {
    router.replace({ hash: "#active-routes" });
  }

  if (value === TruckViewTab.completedRoutes) {
    router.replace({ hash: "#completed-routes" });
  }

  if (value === TruckViewTab.general) {
    router.replace({ hash: "" });
  }
});

onMounted(() => {
  loadTruck();
  setTabByDefault();
});
</script>

<template>
  <v-container v-if="!entityNotFound" :fluid="true" class="pt-6 pb-10 pl-6 pr-6 height-100">
    <sidebar-layout v-if="!loading && truck" :sidebar-opened="isSidebarOpenDefault()">
      <div class="w-100">
        <div class="d-flex justify-space-between align-center flex-wrap mb-4">
          <div class="d-flex">
            <div class="text-grey-darken-4 font-weight-medium text-h6" data-qa="truck-number">
              Truck {{ truck.number }}
            </div>
            <v-chip v-if="truck.archived !== null" :label="true" class="ml-4" color="grey-darken-2" variant="flat">
              Archived
            </v-chip>
          </div>
          <div class="d-flex">
            <omni-tooltip :disabled="hasAccessToEdit">
              <template #activator>
                <v-btn
                  v-if="truck.id && editLink"
                  :class="{ 'btn--disabled': truck.archived }"
                  :disabled="!!truck.archived || !hasAccessToEdit"
                  :to="editLink"
                  class="text-uppercase font-weight-600 mr-2"
                  color="primary"
                  variant="flat"
                >
                  <v-icon class="mr-2" size="20"> mdi-pencil-outline</v-icon>
                  Edit Truck
                </v-btn>
              </template>

              {{ t("permission.edit-truck") }}
            </omni-tooltip>
            <sidebar-toggle class="mr-2"></sidebar-toggle>
            <truck-additional-actions :truck="truck" @change="loadTruck"></truck-additional-actions>
          </div>
        </div>

        <v-card class="bg-transparent" variant="flat">
          <omni-tabs v-model="tab" class="rounded">
            <v-tab :base-color="tab === TruckViewTab.general ? 'primary' : ''" value="general">General </v-tab>
            <v-tab :base-color="tab === TruckViewTab.activeRoutes ? 'primary' : ''" value="activeRoutes">
              Active routes
            </v-tab>
            <v-tab :base-color="tab === TruckViewTab.completedRoutes ? 'primary' : ''" value="completedRoutes">
              Completed routes
            </v-tab>
          </omni-tabs>

          <v-window v-model="tab" :touch="false" class="mt-4">
            <v-window-item :value="TruckViewTab.general">
              <div class="h-100">
                <div class="box-shadow pa-6 bg-white rounded mb-6">
                  <div class="text-grey-darken-3 mb-6 font-weight-medium text-subtitle-1">Characteristics</div>
                  <div class="characteristics-items">
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Make</span>
                      <span class="text-grey-darken-3" data-qa="truck-make">{{ truck.make }}</span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Model</span>
                      <span class="text-grey-darken-3" data-qa="truck-model">{{ truck.model }}</span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Gross weight</span>
                      <span class="text-grey-darken-3" data-qa="truck-gross-weight">{{ truck.grossWeight }}</span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">VIN code</span>
                      <span class="text-grey-darken-3" data-qa="truck-vin-code">{{ truck.vin }}</span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Year</span>
                      <span class="text-grey-darken-3" data-qa="truck-year">{{ truck.year }}</span>
                    </div>
                    <div v-if="!loading" class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">External tracking</span>
                      <truck-tracking-status :enable-tracking="truck.enableTracking" />
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">License plate</span>
                      <span class="text-grey-darken-3" data-qa="truck-licence-plate">{{ truck.licencePlate }}</span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">License state</span>
                      <span class="text-grey-darken-3" data-qa="truck-licence-state">{{ truck.licenceState }}</span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">License expire</span>
                      <span class="text-grey-darken-3" data-qa="truck-licence-expire">{{
                        $filters.date(truck.licenceExpire)
                      }}</span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Color</span>
                      <span class="text-grey-darken-3" data-qa="truck-color">{{ truck.color }}</span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Signs</span>
                      <span class="text-grey-darken-3" data-qa="truck-signs">
                        {{ $filters.booleanValue(truck.signs, "Yes", "No") || "-" }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="box-shadow pa-6 bg-white rounded mb-6">
                  <div class="text-grey-darken-3 mb-6 font-weight-medium text-subtitle-1">Trailer</div>
                  <div class="characteristics-items">
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Length</span>
                      <span class="text-grey-darken-3" data-qa="truck-trailer-length">
                        {{ truck.trailer?.length ?? "-" }}
                      </span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Width</span>
                      <span class="text-grey-darken-3" data-qa="truck-trailer-width">
                        {{ truck.trailer?.width ?? "-" }}
                      </span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Door width</span>
                      <span class="text-grey-darken-3" data-qa="truck-trailer-door-width">
                        {{ truck.trailer?.doorWidth ?? "-" }}
                      </span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Min width</span>
                      <span class="text-grey-darken-3" data-qa="truck-trailer-min-width">
                        {{ truck.trailer?.minWidth ?? "-" }}
                      </span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Height</span>
                      <span class="text-grey-darken-3" data-qa="truck-trailer-height">
                        {{ truck.trailer?.height ?? "-" }}
                      </span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Door height</span>
                      <span class="text-grey-darken-3" data-qa="truck-trailer-door-height">{{
                        truck.trailer?.doorHeight ?? "-"
                      }}</span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Min height</span>
                      <span class="text-grey-darken-3" data-qa="truck-trailer-min-height">
                        {{ truck.trailer?.minHeight ?? "-" }}
                      </span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Year</span>
                      <span class="text-grey-darken-3" data-qa="truck-trailer-year">
                        {{ truck.trailer?.year ?? "-" }}
                      </span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Payload</span>
                      <span class="text-grey-darken-3" data-qa="truck-trailer-payload">{{
                        truck.trailer?.payload
                      }}</span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Door type</span>
                      <span class="text-grey-darken-3" data-qa="truck-trailer-door-type">
                        {{ doorTypeToString(truck) }}
                      </span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Equipment</span>
                      <span class="text-grey-darken-3" data-qa="truck-trailer-equipment">
                        {{ equipmentToString(truck) }}
                      </span>
                    </div>
                    <div class="d-flex characteristics-item">
                      <span class="text-grey-darken-1 characteristics-label">Additional equipment</span>
                      <span class="text-grey-darken-3" data-qa="truck-trailer-additional-equipment">
                        {{ additionalEquipmentToString(truck) }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="box-shadow pa-6 bg-white rounded mb-2">
                  <div class="text-grey-darken-3 mb-6 font-weight-medium text-subtitle-1">Truck notes</div>
                  <div class="driver-note__text bg-grey-lighten-4 pt-4 pb-4 pr-3 pl-3" style="min-height: 110px">
                    {{ $filters.mdash(truck.note || "") }}
                  </div>
                </div>
              </div>
            </v-window-item>
            <v-window-item :value="TruckViewTab.activeRoutes">
              <truck-active-routes />
            </v-window-item>
            <v-window-item :value="TruckViewTab.completedRoutes">Completed routes</v-window-item>
          </v-window>
        </v-card>
      </div>
      <template #sidebar>
        <truck-view-sidebar :truck="truck" @update:truck="loadTruck"></truck-view-sidebar>
      </template>
    </sidebar-layout>
  </v-container>
  <not-found v-else icon="mdi-truck-remove-outline" text="Truck not found"></not-found>
</template>

<style lang="scss" scoped>
.box-shadow {
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.16);
}

.characteristics-item {
  align-items: center;
  padding: 10px 0;
}

.characteristics-label {
  width: 120px;
  margin-right: 30px;
}

.characteristics-items {
  columns: 2;
  margin: -10px 0;
}
</style>
